import React from 'react'
import { SpreadsheetReducerActions } from 'components/spreadsheet/types'

/**
 * Only show the spreadsheet busy overlay if an operation is still running after
 * this many milliseconds. Fast operations settle before the timer fires and so
 * never flash the overlay; genuinely slow ones (large-table mutations) surface it.
 */
export const BUSY_OVERLAY_DELAY_MS = 500

/**
 * At or above this row count a mutation has a perceptible *synchronous* phase
 * (re-sorting / re-indexing / re-rendering the whole dataset) that blocks the
 * main thread. A delay-timer can't paint over a synchronous freeze, so for these
 * tables we show the overlay eagerly and let it paint before the work begins.
 */
export const LARGE_TABLE_ROW_THRESHOLD = 25_000

/** Resolves after the browser has had a chance to paint (two animation frames). */
const nextPaint = (): Promise<void> =>
  new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())))

/**
 * Run an async spreadsheet mutation behind a delay-gated busy overlay.
 *
 * The overlay (driven by the `refreshing` flag) is shown via a `REFRESH` dispatch
 * only if `operation` has not settled within {@link BUSY_OVERLAY_DELAY_MS}. It is
 * always torn down once the operation settles, whether it resolves or rejects, so
 * the grid never gets stuck in a busy state.
 *
 * The promise/throw behaviour of `operation` is preserved — callers can still
 * `await`, `.then`, or `.catch` the result.
 */
export const withBusyOverlay = async <T>(
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  label: string,
  operation: () => Promise<T>
): Promise<T> => {
  let shown = false
  const timer = setTimeout(() => {
    shown = true
    setSpreadsheetData({ type: 'REFRESH', refreshing: true, label })
  }, BUSY_OVERLAY_DELAY_MS)

  try {
    return await operation()
  } finally {
    clearTimeout(timer)
    if (shown) setSpreadsheetData({ type: 'REFRESH', refreshing: false })
  }
}

/**
 * Run a spreadsheet mutation with the busy overlay, choosing the strategy by table size.
 *
 * - **Large tables** (`totalRows >= LARGE_TABLE_ROW_THRESHOLD`): the operation has a
 *   perceptible synchronous phase, so we know it will be slow. Show the overlay and
 *   give it a frame to paint *before* running `operation`, then keep it up until the
 *   operation settles. This is the only way to surface feedback over a synchronous
 *   main-thread freeze — a delay-timer can't, because the frozen thread can't paint.
 * - **Smaller tables**: fall back to {@link withBusyOverlay}'s delay-gate — the overlay
 *   only appears if `operation` outlives {@link BUSY_OVERLAY_DELAY_MS} (e.g. a slow
 *   network), and never flashes for fast operations.
 *
 * Pass `suppressOverlay` when the caller already surfaces its own progress feedback
 * (e.g. a form's submit button spinner) and the full-grid overlay would be redundant.
 */
export const runSpreadsheetMutation = async <T>(
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>,
  label: string,
  totalRows: number,
  operation: () => Promise<T>,
  suppressOverlay = false
): Promise<T> => {
  if (suppressOverlay) {
    return operation()
  }

  if (totalRows < LARGE_TABLE_ROW_THRESHOLD) {
    return withBusyOverlay(setSpreadsheetData, label, operation)
  }

  setSpreadsheetData({ type: 'REFRESH', refreshing: true, label })
  await nextPaint()
  try {
    return await operation()
  } finally {
    setSpreadsheetData({ type: 'REFRESH', refreshing: false })
  }
}
